import { Injectable } from '@angular/core';
import { User as AuthenticatedUser } from 'shared/services/authenticatedUser/types';
import { DynamicFeatureFlagService, OrganizationType, Plan } from 'shared/services';
import { ApiModel } from './api.model';
import { ManufacturerPrivilege, RetailerPrivilege } from 'helpers/team-user-roles';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { CrmServiceModel } from './crm-service.model';

const ENABLE_BLOCK_USER_CRM = 'enable_block_user_crm';
const ORGANIZATION_LIST_DEFAULT_PAGE = 1;
const ORGANIZATION_LIST_DEFAULT_LIMIT = 100;

@Injectable()
export class AdminV2Model {
    constructor(
        private apiModel: ApiModel,
        private crmServiceModel: CrmServiceModel,
        private dynamicFeatureFlagService: DynamicFeatureFlagService
    ) {}

    searchUsers({
        query,
        organizationIds,
        organizationTypes,
        privileges,
        privilegesExact,
        plans,
        sortBy,
        sortOrder,
        cursor,
        limit
    }: {
        query?: string;
        organizationIds?: string[];
        organizationTypes?: OrganizationType[];
        privileges?: string[];
        privilegesExact?: string[];
        plans?: Plan[];
        sortBy?: string;
        sortOrder?: string;
        cursor?: string;
        limit?: number;
    }): Observable<{ users: InternalUserObject[]; cursor: string; total: number }> {
        const queryParams = new URLSearchParams();
        if (query) queryParams.set('query', query);
        organizationIds?.forEach((organizationId) => queryParams.append('organizationIds', organizationId));
        organizationTypes?.forEach((organizationType) => queryParams.append('organizationTypes', organizationType));
        privileges?.forEach((privilege) => queryParams.append('privileges', privilege));
        privilegesExact?.forEach((privilege) => queryParams.append('privilegesExact', privilege));
        plans?.forEach((plan) => queryParams.append('plans', plan));
        if (sortBy) queryParams.set('sortBy', sortBy);
        if (sortOrder) queryParams.set('sortOrder', sortOrder);
        if (cursor) queryParams.set('cursor', cursor);
        if (limit) queryParams.set('limit', limit.toString());

        return this.crmServiceModel.makeHttpObservableRequest<{
            users: InternalUserObject[];
            cursor: string;
            total: number;
        }>({
            method: 'GET',
            url: `${this.crmServiceModel.getUsersEndpoint()}?${queryParams.toString()}`
        });
    }

    /**
     * Browse/list organizations for Admin filters (paginated crm-service organizations API).
     */
    listOrganizations({
        name,
        page = ORGANIZATION_LIST_DEFAULT_PAGE,
        limit = ORGANIZATION_LIST_DEFAULT_LIMIT
    }: {
        name?: string;
        page?: number;
        limit?: number;
    } = {}): Observable<AdminOrganizationListResult> {
        const queryParams = new URLSearchParams();
        queryParams.set('page', page.toString());
        queryParams.set('limit', limit.toString());
        if (name?.trim()) {
            queryParams.set('name', name.trim());
        }

        return this.crmServiceModel
            .makeHttpObservableRequest<AdminOrganizationListResponse>({
                method: 'GET',
                url: `${this.crmServiceModel.getOrganizationsEndpoint()}?${queryParams.toString()}`
            })
            .pipe(map((response) => mapOrganizationListResult(response)));
    }

    getOrganization(organizationId: string): Observable<AdminOrganizationSummary> {
        return this.crmServiceModel
            .makeHttpObservableRequest<AdminOrganizationSummary>({
                method: 'GET',
                url: `${this.crmServiceModel.getOrganizationsEndpoint()}/${organizationId}`
            })
            .pipe(
                map((organization) => ({
                    _id: organization._id,
                    name: organization.name,
                    type: organization.type
                }))
            );
    }

    /**
     * Admin search still returns legacy/backend user ids. The crm-service blocked
     * endpoint accepts that id; legacy backend signup keeps using email.
     */
    updateBlockedStatus(userId: string, email: string, blocked: boolean): Observable<void> {
        return this.dynamicFeatureFlagService.isFeatureEnabled$(ENABLE_BLOCK_USER_CRM).pipe(
            take(1),
            switchMap((useCrmService) =>
                useCrmService
                    ? this.crmServiceModel.makeHttpObservableRequest({
                          method: 'PUT',
                          url: `${this.crmServiceModel.getUsersEndpoint()}/${userId}/blocked`,
                          body: { blocked }
                      })
                    : this.apiModel.makeHttpObservableRequest({
                          method: 'PUT',
                          url: `${this.apiModel.getUsersEndpoint()}/blocked`,
                          body: { username: email, blocked }
                      })
            )
        );
    }

    updateOrganizationPlan(organizationId: string, plan: Plan): Observable<null> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'PUT',
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/plan`,
            body: { plan }
        });
    }

    impersonateUser(email: string, organizationId: string): Observable<AuthenticatedUser> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url: `${this.apiModel.getSigninEndpoint()}/adminAccess`,
            body: { email, organizationId },
            withCredentials: true
        });
    }

    updateEcommercePackage(organizationId: string, packageValue: boolean): Observable<null> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'PUT',
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/packages`,
            body: {
                package: 'ecommerce',
                value: packageValue
            }
        });
    }

    activateAccount(userId: string): Observable<null> {
        return this.apiModel.makeHttpObservableRequest({
            method: 'POST',
            url: `${this.apiModel.getUsersEndpoint()}/action/activateAccount`,
            body: { _id: userId }
        });
    }
}

export interface InternalUserObject {
    _id: string;
    email: string;
    emailVerified: boolean;
    type: OrganizationType;
    created: string;
    blocked: boolean;
    firstname: string;
    surname: string;
    privileges: ManufacturerPrivilege[] | RetailerPrivilege[];
    organization: {
        _id: string;
        name: string;
        plan: Plan;
        phone: string;
        type: OrganizationType;
        hasEcommercePackage: boolean;
    };
}

export interface AdminOrganizationSummary {
    _id: string;
    name: string;
    type?: OrganizationType;
}

export interface AdminOrganizationListResponse {
    data: AdminOrganizationSummary[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
}

export interface AdminOrganizationListResult {
    organizations: AdminOrganizationSummary[];
    totalItems: number;
    totalPages: number;
    currentPage: number;
}

export function mapOrganizationListResult(response: AdminOrganizationListResponse): AdminOrganizationListResult {
    return {
        organizations: (response?.data ?? []).map((organization) => ({
            _id: organization._id,
            name: organization.name,
            type: organization.type
        })),
        totalItems: response?.totalItems ?? 0,
        totalPages: response?.totalPages ?? 0,
        currentPage: response?.currentPage ?? ORGANIZATION_LIST_DEFAULT_PAGE
    };
}
